import { useQuery } from '@tanstack/react-query';
import { FC, useCallback, useMemo } from 'react';

import { _getMemberActivity, _getTeamActivity } from '@/api/gamma-team/queries';
import { activityPeriod } from '@/api/gamma-team/types';
import { useGetUserProfile } from '@/api/user-dashboard/queries';
import { Button, DatePicker, type DateRange, theme, Typography } from '@/design-system';
import { Permission, usePermissions } from '@/shared/modules/permissions';

import { MemberActivity } from './member-activity';
import {
  ACTIVITY_DAY_FORMAT,
  DEFAULT_PERIOD,
  resolveTimeZone,
  toActivityDateRange,
  toActivityParams,
} from './period';
import { QuietLine, Section, SectionHeader, TabRoot } from './styled';
import { TeamActivityFilters } from './team-activity-filters';
import { TeamTable } from './team-table';
import { useTeamActivityFilters } from './use-team-activity-filters';

export const INCOMPLETE_RANGE_HINT = 'Pick a start and an end date to run the report.';

export const TeamActivityTab: FC = () => {
  const { has } = usePermissions();
  const canViewTeam = has(Permission.AdminTeamActivityView);
  const { user } = useGetUserProfile();
  const ownUserId = user?.id ?? null;
  const timeZone = useMemo(resolveTimeZone, []);

  const filters = useTeamActivityFilters({ canViewTeam, ownUserId });
  const { period, from, to, members } = filters;
  const params = useMemo(
    () => toActivityParams({ period, from, to }, timeZone),
    [period, from, to, timeZone],
  );
  const requestParams = params ?? { period: DEFAULT_PERIOD, timeZone };

  const memberId = members?.length === 1 ? members[0] : null;
  const selectedIds = useMemo(
    () => (members && members.length > 1 ? [...members].sort() : null),
    [members],
  );

  const teamQuery = useQuery({
    ..._getTeamActivity(requestParams),
    enabled: canViewTeam && params !== null,
  });

  const selectionQuery = useQuery({
    ..._getTeamActivity(selectedIds ? { ...requestParams, memberIds: selectedIds } : requestParams),
    enabled: canViewTeam && selectedIds !== null && params !== null,
  });

  const memberQuery = useQuery({
    ..._getMemberActivity(memberId ?? '', requestParams),
    enabled: memberId !== null && params !== null,
  });

  const tableQuery = selectedIds ? selectionQuery : teamQuery;
  const range = memberId ? memberQuery.data?.range : tableQuery.data?.range;
  const currentMember = memberQuery.data?.member ?? null;
  const today = useMemo(() => new Date(), []);
  const shownRange = useMemo((): DateRange | null => {
    if (period === activityPeriod.Custom) return { from, to };
    return range ? toActivityDateRange(range) : null;
  }, [period, from, to, range]);

  const handleShownRangeChange = (next: DateRange) => {
    if (next.from || next.to) {
      filters.setRange(next);
    } else {
      filters.setPeriod(DEFAULT_PERIOD);
    }
  };

  const { setMembers } = filters;
  const selectOneMember = useCallback((userId: string) => setMembers([userId]), [setMembers]);

  return (
    <TabRoot>
      <Section>
        <SectionHeader>
          <Typography variant="h5">Team activity</Typography>
          {shownRange && (
            <DatePicker
              months="range"
              placeholder="Select dates"
              dateFormat={ACTIVITY_DAY_FORMAT}
              maxDate={today}
              value={shownRange}
              onChange={handleShownRangeChange}
            />
          )}
        </SectionHeader>

        <TeamActivityFilters
          period={filters.period}
          from={filters.from}
          to={filters.to}
          members={members}
          canViewTeam={canViewTeam}
          options={teamQuery.data?.members ?? []}
          currentMember={currentMember}
          onPeriodChange={filters.setPeriod}
          onRangeChange={filters.setRange}
          onMembersChange={setMembers}
        />
      </Section>

      {params === null ? (
        <QuietLine role="status">
          <Typography variant="body2" color={theme.colors.text.secondary}>
            {INCOMPLETE_RANGE_HINT}
          </Typography>
        </QuietLine>
      ) : memberId ? (
        <MemberActivity
          key={memberId}
          data={memberQuery.data}
          isPending={memberQuery.isPending}
          isError={memberQuery.isError}
          isFetching={memberQuery.isFetching}
          onRetry={() => memberQuery.refetch()}
        />
      ) : tableQuery.isError && !tableQuery.data ? (
        <QuietLine role="alert">
          <Typography variant="body2" color={theme.colors.text.secondary}>
            Team activity couldn’t be loaded.
          </Typography>
          <Button
            variant="outline"
            color="tertiary"
            size="md"
            disabled={tableQuery.isFetching}
            onClick={() => tableQuery.refetch()}
          >
            {tableQuery.isFetching ? 'Loading…' : 'Try again'}
          </Button>
        </QuietLine>
      ) : (
        <TeamTable
          members={tableQuery.data?.members ?? []}
          total={tableQuery.data?.total ?? null}
          isLoading={members === null || tableQuery.isPending}
          onSelectMember={selectOneMember}
        />
      )}
    </TabRoot>
  );
};

export default TeamActivityTab;
